import { Box, Button, TextField } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { API_BASE_URL } from "../../config/api";
import { assertPartnerPortalAccess, extractLoginRole } from "../../utils/authRole";

// Validation schema for partner login
const partnerLoginSchema = yup.object().shape({
  login: yup.string().required("Login is required"),
  password: yup.string().required("Password is required"),
});

const PartnerLoginForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFormSubmit = async (values) => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/company/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          login: values.login,
          password: values.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      assertPartnerPortalAccess(extractLoginRole(data));

      // Save token to localStorage
      if (data.items && data.items.token) {
        localStorage.setItem("authToken", data.items.token);
        localStorage.setItem("userRole", "partner");
        // Navigate to partner dashboard
        navigate("/partner-dashboard", { state: { token: data.items.token } });
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      console.error("Partner login error:", err);
      setError(err.message || "An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box m="20px">
      <Header title="Partner Login" subtitle="Login to your partner account" />

      {/* Display error message */}
      {error && (
        <Box
          color="error.main"
          p={2}
          mb={2}
          sx={{ backgroundColor: "rgba(211, 47, 47, 0.1)", borderRadius: "4px" }}
        >
          {error}
        </Box>
      )}

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={partnerLoginSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Login"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.login}
                name="login"
                error={!!touched.login && !!errors.login}
                helperText={touched.login && errors.login}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="password"
                label="Password"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password}
                name="password"
                error={!!touched.password && !!errors.password}
                helperText={touched.password && errors.password}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>
            <Box display="flex" justifyContent="space-between" mt="20px">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate("/")}
              >
                Back
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const initialValues = {
  login: "",
  password: "",
};

export default PartnerLoginForm;

